import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';

import { EFEITOS } from '../tokens';

/**
 * Efeito holografico da carta, so no ecra: a carta inclina-se para o rato (ou
 * para o dedo, na horizontal) e um reflexo e um brilho correm por cima. O SVG
 * nao muda, por isso a imagem descarregada sai parada, so com a textura.
 *
 * Cada carta so anima enquanto o ponteiro esta em cima e ate assentar; numa
 * grelha de 48 cartas so a que esta debaixo do rato gasta alguma coisa. Com
 * «reduzir movimento» no sistema nao ha inclinacao (ver styles.css).
 *
 * `live` diz se a carta esta a ser mexida (do primeiro toque do ponteiro ate
 * voltar a assentar): com `holo="hover"` as camadas so existem enquanto e true.
 */

/** O brilho de cada tipo de conta: quanto mais especial, mais forte. */
export type HoloKind = 'user' | 'member' | 'associate';

type Point = { x: number; y: number; a: number };

const REST: Point = { x: 0.5, y: 0.5, a: 0 };

export function useHolo() {
  const el = useRef<HTMLSpanElement>(null);
  const cur = useRef<Point>({ ...REST });
  const target = useRef<Point>({ ...REST });
  const frame = useRef<number | null>(null);
  const hover = useRef(false);
  const [live, setLive] = useState(false);

  const step = useCallback(() => {
    const node = el.current;
    const c = cur.current;
    const t = target.current;
    const f = hover.current ? 0.18 : 0.08;
    c.x += (t.x - c.x) * f;
    c.y += (t.y - c.y) * f;
    c.a += (t.a - c.a) * f;
    if (node) {
      const dx = c.x - 0.5;
      const dy = c.y - 0.5;
      const s = node.style;
      s.setProperty('--gu-holo-rx', `${(-dy * EFEITOS['holo-inclinacao-x'] * c.a).toFixed(2)}deg`);
      s.setProperty('--gu-holo-ry', `${(dx * EFEITOS['holo-inclinacao-y'] * c.a).toFixed(2)}deg`);
      s.setProperty(
        '--gu-holo-scale',
        (1 + (hover.current ? EFEITOS['holo-zoom'] : 0) * c.a).toFixed(3),
      );
      s.setProperty('--gu-holo-act', c.a.toFixed(3));
      s.setProperty('--gu-holo-mx', `${(c.x * 100).toFixed(1)}%`);
      s.setProperty('--gu-holo-my', `${(c.y * 100).toFixed(1)}%`);
      s.setProperty('--gu-holo-hx', `${(37 + c.x * 26).toFixed(1)}%`);
      s.setProperty('--gu-holo-hy', `${(33 + c.y * 34).toFixed(1)}%`);
    }
    const settled =
      Math.abs(t.x - c.x) < 0.002 && Math.abs(t.y - c.y) < 0.002 && Math.abs(t.a - c.a) < 0.002;
    frame.current = settled ? null : requestAnimationFrame(step);
    if (settled && !hover.current) setLive(false);
  }, []);

  const kick = useCallback(() => {
    if (frame.current === null) frame.current = requestAnimationFrame(step);
  }, [step]);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  const move = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      target.current = {
        x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
        y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
        a: 1,
      };
      hover.current = true;
      setLive(true);
      kick();
    },
    [kick],
  );

  const leave = useCallback(() => {
    target.current = { ...REST };
    hover.current = false;
    kick();
  }, [kick]);

  const up = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      if (e.pointerType !== 'mouse') leave();
    },
    [leave],
  );

  return {
    ref: el,
    live,
    handlers: {
      onPointerEnter: move,
      onPointerMove: move,
      onPointerDown: move,
      onPointerLeave: leave,
      onPointerUp: up,
      onPointerCancel: leave,
    },
  };
}
